import { jsx, jsxs } from 'react/jsx-runtime';
import classNames from 'classnames';
import { forwardRef, useCallback } from 'react';
import { CheckCircle } from '@zooplus/icons';
import { getTestLabels } from '../../../utils/getTestLabels.js';
import { FormControlWrap } from '../FormControlWrap/FormControlWrap.js';
import textInputStyle from '../style/text-input.module.scss.js';
import inputStyle from './Input.module.scss.js';

const { componentTestLabel, elementTestLabel } = getTestLabels('Input');
/**
 * [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=2718-4192&node-type=section&t=L12XIxh9aK1lGzgC-0)
 * | [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Form/Input/Input.tsx)
 *
 * ### **Note:** see the [InputField](https://ui-components.private.zooplus.net/?path=/docs/components-form-inputfield--docs) component for a more composed and fully accessible single `<input>` with associated label and hint and/or error messages.
 *
 * @param {boolean} hasError - (optional) Set to `true` when the input is in an error state. Applies visual error styling.
 * @param {ReactNode} icon - (optional) Should be an SVG icon from the [@zooplus/icons](https://icons.private.zooplus.net/) package.
 * @param {object} inputProps - (optional) An object of native HTML input attributes to pass to the input element (see [MDN Input docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input)).
 * @param {boolean} isDisabled - (optional) Set to `true` to visually and functionally disable the input.
 * @param {boolean} isValid- (optional) Whether or not the entered field value has been validated/verified.
 * @param {boolean} isReadOnly- (optional) Set to `true` when the input is intended to be read-only. Applies visual styling.
 * @param {function} onChange - (optional) A custom function to call when the input value is changed.
 * @param {function} renderInput - (optional) Use this to pass a completely custom `<input/>` element.
 */
const Input = forwardRef((props, ref) => {
    const { hasError = false, inputProps, icon, isDisabled = false, isValid = false, isReadOnly = false, onChange, renderInput, ...otherProps } = props;
    const hasIcon = !!icon || isValid;
    const defaultRenderInput = useCallback((props, ref) => {
        return (jsx("input", { "data-zta": elementTestLabel('Input'), ...props, ref: ref }));
    }, []);
    return (jsxs(FormControlWrap, { "data-zta": componentTestLabel, ...otherProps, children: [(renderInput || defaultRenderInput)({
                ...inputProps,
                className: classNames(textInputStyle['z-text-input'], inputStyle['z-input'], inputProps?.className, {
                    [textInputStyle['z-text-input--error']]: hasError,
                    [inputStyle['z-input--has-icon']]: hasIcon,
                }),
                onChange: onChange || inputProps?.onChange,
                disabled: isDisabled || inputProps?.disabled,
                readOnly: isReadOnly || inputProps?.readOnly,
                type: inputProps?.type || 'text',
            }, ref), isValid && (jsx("i", { className: inputStyle['z-input-icon'], children: jsx(CheckCircle, { "aria-hidden": true }) })), !isValid && icon && jsx("i", { className: inputStyle['z-input-icon'], children: icon })] }));
});
Input.displayName = 'Input';

export { Input };
//# sourceMappingURL=Input.js.map
